import { useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useQuery } from 'convex/react'
import { ChevronDown, FileText, MoreVertical, Plus, Search, X } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { api } from '../../../convex/_generated/api'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

type Tab = 'all' | 'mine' | 'example'

type Skill = {
  name: string
  description: string
  content: string
}

export default function SkillsPage() {
  const [tab, setTab] = useState<Tab>('all')
  const [query, setQuery] = useState('')
  const [activeSkill, setActiveSkill] = useState<Skill | null>(null)

  const skills = useQuery(api.skills.listSkills, {})
  const isLoading = skills === undefined

  // Every built-in skill counts as an "example". User-authored skills don't
  // exist yet (creation is coming soon), so the My skills tab is always empty.
  const allSkills = skills ?? []
  const exampleSkills = allSkills
  const mySkills: typeof allSkills = []

  const visible = useMemo(() => {
    const source = tab === 'mine' ? mySkills : tab === 'example' ? exampleSkills : allSkills
    const q = query.trim().toLowerCase()
    if (!q) return source
    return source.filter((s) =>
      s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q),
    )
  }, [tab, query, allSkills, exampleSkills, mySkills])

  return (
    <div className="px-8 pt-10 pb-16">
      <Helmet>
        <title>Skills — Footage</title>
        <meta name="description" content="Extend what Footage can do with skills." />
      </Helmet>

      {/* Header */}
      <div className="flex items-start justify-between gap-6 mb-6 max-w-[1100px] mx-auto w-full">
        <div>
          <h1 className="text-[22px] leading-tight font-medium tracking-tight text-foreground">Skills</h1>
          <p className="mt-1 text-[14px] text-muted-foreground">Extend what Footage can do with skills.</p>
        </div>
        <div className="relative shrink-0 hidden md:block">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/50" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search skills"
            className="h-10 w-[300px] rounded-full border border-border bg-white pl-10 pr-4 text-[14px] text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-ring"
          />
        </div>
      </div>

      {/* Filter pills + Create skill */}
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap max-w-[1100px] mx-auto">
        <div className="inline-flex items-center gap-1.5">
          {([
            { key: 'all', label: 'All' },
            { key: 'mine', label: 'My skills' },
            { key: 'example', label: 'Included skills' },
          ] as { key: Tab; label: string }[]).map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={cn(
                'px-3.5 py-1.5 text-[13px] rounded-full transition-colors',
                tab === t.key
                  ? 'bg-muted text-foreground font-medium'
                  : 'text-foreground hover:bg-muted/60',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative group">
          <button
            type="button"
            disabled
            aria-disabled
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-border text-[13px] font-medium text-muted-foreground cursor-not-allowed"
          >
            <Plus className="h-4 w-4" />
            Create skill
            <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-muted text-muted-foreground">
              Coming soon
            </span>
          </button>
        </div>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="text-[14px] text-muted-foreground/50 mt-12">Loading skills…</div>
      ) : visible.length === 0 ? (
        <EmptySkills tab={tab} query={query} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 max-w-[1100px] mx-auto">
          {visible.map((skill) => (
            <SkillCard
              key={skill.name}
              name={skill.name}
              description={skill.description}
              onViewDetails={() =>
                setActiveSkill({
                  name: skill.name,
                  description: skill.description,
                  content: skill.content,
                })
              }
            />
          ))}
        </div>
      )}

      <SkillDetailsDialog
        skill={activeSkill}
        onOpenChange={(open) => {
          if (!open) setActiveSkill(null)
        }}
      />
    </div>
  )
}

function SkillCard({
  name,
  description,
  onViewDetails,
}: {
  name: string
  description: string
  onViewDetails: () => void
}) {
  return (
    <div className="group relative rounded-2xl border border-border bg-white px-5 py-4 hover:border-ring transition-colors">
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={onViewDetails}
          className="min-w-0 flex-1 text-left focus:outline-none"
        >
          <h3 className="text-[15px] font-medium text-foreground truncate">{name}</h3>
          <p
            className="mt-1.5 text-[13px] leading-snug text-muted-foreground overflow-hidden"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {description}
          </p>
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`More actions for ${name}`}
              className="shrink-0 p-1 rounded-md text-muted-foreground/50 hover:text-foreground hover:bg-muted/60 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 data-[state=open]:opacity-100"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={6}
            className="min-w-[180px] rounded-xl border border-border bg-white p-1.5 shadow-[0_8px_28px_rgba(31,28,24,0.08)]"
          >
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault()
                onViewDetails()
              }}
              className="gap-2.5 rounded-lg px-2.5 py-2 text-[13px] text-foreground focus:bg-muted focus:text-foreground"
            >
              <FileText className="h-4 w-4 text-muted-foreground" />
              View details
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

function SkillDetailsDialog({
  skill,
  onOpenChange,
}: {
  skill: Skill | null
  onOpenChange: (open: boolean) => void
}) {
  return (
    <DialogPrimitive.Root open={skill !== null} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-neutral-200/60 backdrop-blur-[1px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 z-50 w-[min(1100px,calc(100vw-32px))] h-[min(820px,calc(100vh-48px))] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border border-neutral-300 bg-card shadow-[0_12px_30px_-12px_rgba(0,0,0,0.14)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          aria-describedby={undefined}
        >
          {skill && (
            <div className="flex h-full flex-col">
              {/* Header */}
              <div className="flex items-center justify-between gap-3 border-b border-border px-6 py-4">
                <DialogPrimitive.Title className="text-[15px] font-medium text-foreground truncate">
                  {skill.name}
                </DialogPrimitive.Title>
                <DialogPrimitive.Close
                  className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors focus:outline-none"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </DialogPrimitive.Close>
              </div>

              {/* Body */}
              <div className="flex flex-1 min-h-0">
                {/* Sidebar */}
                <aside className="w-[280px] shrink-0 border-r border-border bg-muted/30 overflow-y-auto">
                  <SidebarSection title="About" defaultOpen>
                    <p className="text-[13px] leading-relaxed text-[#3f3a33]">
                      {skill.description}
                    </p>
                  </SidebarSection>
                  <SidebarSection title="Files" defaultOpen>
                    <button
                      type="button"
                      className="flex items-center gap-2 w-full text-left px-2.5 py-1.5 rounded-md bg-muted text-[13px] text-foreground"
                    >
                      <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                      SKILL.md
                    </button>
                  </SidebarSection>
                </aside>

                {/* Main content */}
                <main className="flex-1 min-w-0 overflow-y-auto">
                  <div className="mx-auto max-w-[760px] px-10 py-8">
                    {/* Frontmatter */}
                    <div className="text-[14px] leading-relaxed text-foreground pb-5 border-b border-border">
                      <div>
                        <span className="text-muted-foreground">name:</span> {skill.name}
                      </div>
                      <div className="mt-1">
                        <span className="text-muted-foreground">description:</span> {skill.description}
                      </div>
                    </div>

                    {/* Markdown body */}
                    <div className="pt-6 skill-markdown">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {skill.content}
                      </ReactMarkdown>
                    </div>
                  </div>
                </main>
              </div>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

function SidebarSection({
  title,
  defaultOpen = true,
  children,
}: {
  title: string
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="px-4 py-3 border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 transition-transform',
            !open && '-rotate-90',
          )}
        />
        {title}
      </button>
      {open && <div className="mt-2.5 pl-1">{children}</div>}
    </div>
  )
}

function EmptySkills({ tab, query }: { tab: Tab; query: string }) {
  const message = query
    ? `No skills match "${query}".`
    : tab === 'mine'
      ? "You haven't created any skills yet. Skill authoring is coming soon."
      : 'No skills available.'

  return (
    <div className="max-w-[1100px] mx-auto w-full text-center mt-16">
      <p className="text-[14px] text-muted-foreground">{message}</p>
    </div>
  )
}
